import { PropsWithChildren, ReactNode } from 'react';
import { Box, Button, Skeleton, Stack, styled, Typography } from '@mui/material';

import { ReportTierOptions } from '@StandingAccess/types';

import { AssessmentTierWidgetLabel } from './AssessmentTierWidgetLabel';

const AssessmentTierWidgetWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(2, 3),
  border: `1px solid ${theme.palette.background.paper}`,

  '&.clickable': {
    cursor: 'pointer',

    '&:hover': {
      backgroundColor: theme.palette.background.active,
    },
  },

  '&.active': {
    backgroundColor: theme.palette.background.active,
    borderColor: theme.palette.primary.main,
  },
}));

interface AssessmentTierWidgetProps {
  type: ReportTierOptions;
  title: string;
  subTitle?: string;
  description: string;
  active?: boolean;
  disabled?: boolean;
  remediation?: ReactNode;
  action?: ReactNode;
  onClick?: () => void;
}

export default function AssessmentTierWidget({
  type,
  title,
  subTitle,
  description,
  active,
  disabled,
  onClick,
  children,
}: PropsWithChildren<AssessmentTierWidgetProps>) {
  const className = [];
  if (active) className.push('active');
  if (!disabled && onClick) className.push('clickable');

  return (
    <AssessmentTierWidgetWrapper onClick={disabled ? undefined : onClick} className={className.join(' ')}>
      <Stack direction="column" spacing={1.5}>
        <AssessmentTierWidgetLabelHeader type={type} description={description} />
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography variant="h6" color={disabled ? 'text.muted' : 'text.primary'}>
            {title}
          </Typography>
          {subTitle && (
            <Typography variant="helperText" color="text.muted">
              {subTitle}
            </Typography>
          )}
        </Stack>
        {children}
      </Stack>
    </AssessmentTierWidgetWrapper>
  );
}

export function AssessmentTierWidgetLoading({ type }: Pick<AssessmentTierWidgetProps, 'type'>) {
  return (
    <AssessmentTierWidgetWrapper>
      <Stack direction="column" spacing={1.5}>
        <Stack direction="row" justifyContent="space-between">
          <AssessmentTierWidgetLabel type={type} />
        </Stack>
        <Skeleton variant="text" width="100%" height={32} />
        <Button variant="outlined" color="secondary" disabled>
          Remediation Plan
        </Button>
      </Stack>
    </AssessmentTierWidgetWrapper>
  );
}

export function AssessmentTierWidgetLabelHeader({
  type,
  description,
}: Pick<AssessmentTierWidgetProps, 'type' | 'description'>) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <AssessmentTierWidgetLabel type={type} />
      <Typography variant="helperText" color="text.muted">
        {description}
      </Typography>
    </Stack>
  );
}
